import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn, getTestId } from '../utils';
import { useTranslation } from '../providers/UIProvider';
import { Icon } from './icons/Icon';
import { IconName } from '../icons/names';

const buttonVariants = cva(
  `inline-flex items-center justify-center
   !cursor-pointer
   whitespace-nowrap font-regular
   focus-visible:outline focus-visible:outline-2
   focus-visible:outline-offset-2
   transition-all
   `,
  {
    variants: {
      variant: {
        primary:
          'bg-primary-base text-foreground-white hover:bg-primary-hover active:bg-primary-hover',
        secondary:
          'bg-secondary-base text-foreground-white hover:bg-secondary-hover active:bg-secondary-hover',
        error: 'bg-error-text text-foreground-white hover:bg-error-border active:bg-error-border',
        warning: 'bg-warn-text text-foreground-white hover:bg-warn-border active:bg-warn-border',
        success:
          'bg-success-text text-foreground-white hover:bg-success-border active:bg-success-border',
        default:
          'bg-background-input text-foreground-base hover:bg-background-muted active:bg-background-muted disabled:bg-background-disabled',
        ghost:
          'bg-transparent text-foreground-base hover:bg-background-muted active:bg-background-muted disabled:bg-background-disabled disabled:text-foreground-disabled'
      },
      outline: {
        primary:
          'border border-primary-base bg-transparent text-primary-base hover:bg-primary-base active:bg-primary-hover hover:text-foreground-white',
        secondary:
          'border border-secondary-base bg-transparent text-secondary-base hover:bg-secondary-base active:bg-secondary-hover hover:text-foreground-white',
        error:
          'border border-error-text bg-transparent text-error-text hover:bg-error-text active:bg-error-border hover:text-foreground-white',
        warning:
          'border border-warn-text bg-transparent text-warn-text hover:bg-warn-text active:bg-warn-border hover:text-foreground-white',
        success:
          'border border-success-text bg-transparent text-success-text hover:bg-success-text active:bg-success-border hover:text-foreground-white',
        neutral: '',
        default:
          'border border-border-base bg-transparent text-foreground-base hover:bg-background-muted active:bg-background-muted',
        ghost:
          'border border-transparent bg-transparent text-foreground-base hover:bg-background-muted active:bg-background-muted'
      },
      size: {
        sm: 'h-8 px-md text-sm rounded-md',
        cm: 'h-9 px-md text-base rounded-md',
        md: 'h-9 px-md text-base rounded-lg',
        lg: 'h-10 px-lg text-xl rounded-lg',
        xl: 'h-12 px-lg text-2xl rounded-xl',
        full: 'w-full h-10 rounded-lg',
        icon: 'p-2xs rounded'
      },
      orientation: {
        horizontal: 'flex-row min-h-[var(--button-height)]',
        vertical: 'flex-col items-center justify-center h-auto p-sm'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      orientation: 'horizontal'
    }
  }
);

type ButtonVariants = VariantProps<typeof buttonVariants>;

type BaseButtonAttributes = Pick<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  | 'onClick'
  | 'onMouseDown'
  | 'className'
  | 'disabled'
  | 'children'
  | 'type'
  | 'form'
  | 'id'
  | 'name'
  | 'role'
  | 'aria-label'
  | 'aria-hidden'
  | 'aria-expanded'
  | 'aria-selected'
  | 'aria-current'
  | 'aria-disabled'
  | 'aria-describedby'
  | 'tabIndex'
  | 'title'
>;

export interface ButtonProps extends BaseButtonAttributes, ButtonVariants {
  locKey?: string | null;
  leftIcon?: React.ReactNode;
  icon?: IconName | React.ReactNode;
  iconPosition?: 'left' | 'right';
  testId?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    variant,
    size,
    className,
    outline,
    orientation = 'horizontal',
    locKey,
    disabled,
    children,
    onClick,
    leftIcon,
    icon,
    iconPosition = 'left',
    type = 'button',
    testId,
    id,
    name,
    ...rest
  } = props;
  const t = useTranslation();
  // Stays focusable, unlike `disabled`; whoever sets it guards its own click.
  const ariaDisabled = rest['aria-disabled'] === true || rest['aria-disabled'] === 'true';

  const finalIcon: React.ReactNode | undefined = leftIcon ?? icon;
  const finalIconPosition: 'left' | 'right' = leftIcon ? 'left' : iconPosition;
  const hasIcon = !!finalIcon;
  const label: React.ReactNode = locKey ? t(`button.${locKey}`) : children;
  const hasLabel = label !== null && label !== undefined && label !== false && label !== '';

  const iconSizeClass = 'w-4 h-4';
  const iconColorClass = 'fill-current text-current';

  const renderIcon = (node: React.ReactNode) => {
    if (typeof node === 'string') {
      return (
        <Icon
          name={node as IconName}
          className={cn(iconSizeClass, iconColorClass)}
          aria-hidden='true'
        />
      );
    }
    return (
      <span aria-hidden='true' className={cn(iconSizeClass, iconColorClass)}>
        {node}
      </span>
    );
  };

  return (
    <button
      type={type}
      ref={ref}
      id={id}
      name={name}
      data-testid={getTestId({ testId, id, name })}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        buttonVariants({ variant, size, orientation, outline }),
        className,
        (disabled || ariaDisabled) && 'opacity-50',
        hasIcon && hasLabel && (orientation === 'horizontal' ? 'gap-sm' : 'gap-xs')
      )}
      {...rest}
    >
      {finalIcon && finalIconPosition === 'left' ? renderIcon(finalIcon) : null}

      {hasLabel ? (
        <span className={cn(orientation === 'horizontal' ? 'whitespace-nowrap' : 'text-center')}>
          {label}
        </span>
      ) : null}

      {finalIcon && finalIconPosition === 'right' ? renderIcon(finalIcon) : null}
    </button>
  );
});

Button.displayName = 'Button';
